import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid2, Typography } from "@mui/material";
import ReactCountryFlag from "react-country-flag";
import { format, parseISO } from "date-fns";
import { Badge } from "@components";
import { toShort } from "@helpers";
import CoinsGrowthIcon from "@icons/myicons/coins-growth.svg?react";
import ChartSquareIcon from "@icons/myicons/chart-square-full.svg?react";
import BuildingsIcon from "@icons/myicons/buildings.svg?react";
import WorldIcon from "@icons/myicons/world.svg?react";
import CodeIcon from "@icons/myicons/code.svg?react";
import ClockIcon from "@icons/myicons/clock-time-arrow.svg?react";
import { FundamentalsBlockLayout } from "../FundamentalsBlockLayout";

interface Props {
  isFetching?: boolean;
  marketCap?: number;
  marketCapCurrency?: string;
  stockIndexes?: string[];
  sectorName?: string;
  lastBalanceUpdate?: string;
  wkn?: string;
  countryTwoLetterCode?: string;
  countryName?: string;
}

const DetailRow = ({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) => (
  <Box>
    <Box
      display="flex"
      alignItems="center"
      gap={1.5}
      mb={0.5}
      color="icon.tertiary"
    >
      {icon}
      <Typography variant="body4" color="textSecondary">
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

const iconProps = { width: 14, height: 14 };

export const DetailInformationBlock = ({
  marketCap,
  marketCapCurrency,
  isFetching,
  stockIndexes,
  sectorName,
  lastBalanceUpdate,
  countryTwoLetterCode,
  wkn,
  countryName,
}: Props) => {
  const { t } = useTranslation();
  const title = t("fundamentals.detailInformation.title");

  return (
    <FundamentalsBlockLayout title={title} isFetching={isFetching}>
      <Grid2 container rowSpacing={3} columnSpacing={2} px={4} pb={4}>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <DetailRow
            icon={<CoinsGrowthIcon {...iconProps} />}
            title={t("fundamentals.detailInformation.marketCap")}
          >
            <Typography variant="body2">
              {marketCap !== undefined ? toShort(marketCap) : "N/A"}
              {marketCapCurrency}
            </Typography>
          </DetailRow>
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <DetailRow
            icon={<ChartSquareIcon {...iconProps} />}
            title={t("fundamentals.detailInformation.indexes")}
          >
            {stockIndexes && stockIndexes.length > 0 ? (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {stockIndexes.map((index) => (
                  <Badge key={index} variant="primary" textVariant="body3">
                    {index}
                  </Badge>
                ))}
              </Box>
            ) : (
              <Typography variant="body2">N/A</Typography>
            )}
          </DetailRow>
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <DetailRow
            icon={<BuildingsIcon {...iconProps} />}
            title={t("fundamentals.detailInformation.sector")}
          >
            <Typography variant="body2">{sectorName || "N/A"}</Typography>
          </DetailRow>
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <DetailRow
            icon={<ClockIcon {...iconProps} />}
            title={t("fundamentals.detailInformation.lastUpdate")}
          >
            <Typography variant="body2">
              {lastBalanceUpdate
                ? format(parseISO(lastBalanceUpdate), "dd.MM.yyyy")
                : "N/A"}
            </Typography>
          </DetailRow>
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <DetailRow icon={<CodeIcon {...iconProps} />} title="WKN">
            <Typography variant="body2">{wkn || "N/A"}</Typography>
          </DetailRow>
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <DetailRow
            icon={<WorldIcon {...iconProps} />}
            title={t("fundamentals.detailInformation.country")}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {countryTwoLetterCode && (
                <ReactCountryFlag countryCode={countryTwoLetterCode} />
              )}
              <Typography variant="body2">{countryName || "N/A"}</Typography>
            </Box>
          </DetailRow>
        </Grid2>
      </Grid2>
    </FundamentalsBlockLayout>
  );
};
